/* 시공사례 사진 주소
   · 인디포토 사진: /api/portfolio/showcase/photos/files/{externalId} — 화면은 sv-photo 를 직접 부르지 않고 항상 sv-portfolio 를 거친다.
     로그인 토큰이 있어야 받을 수 있어서 apiClient로 받아 blob 주소로 바꾼다
   · AI 편집 사진: /api/portfolio/public/showcase/images/{id}?s=서명 — 서명된 공개 주소라 그대로 쓴다
   문서(src)에는 위 주소를 그대로 저장한다(공개 발행 때 서버가 externalId·aiImgId 로 사진을 복사한다). */
import { useEffect, useState } from 'react';
import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';
import type { El, ImageRef } from './types';

const FILES = `${API_PRX.PORTFOLIO}/showcase/photos/files`;
export const photoSrc = (externalId: string) => `/api${FILES}/${externalId}`;

/** 예전 주소(/api/photo/files/…)로 저장된 문서도 같은 사진으로 본다 */
const PHOTO_RE = /\/photos?\/files\/([^/?#]+)/;
const AI_RE = /\/showcase\/images\/(\d+)/;

/** 요소(또는 주소)로 서버에 보낼 사진 정보 */
export function refOf(x: El | string): ImageRef | null {
  const src = typeof x === 'string' ? x : x.src || '';
  if (typeof x !== 'string') {
    if (x.aiImgId) return { aiImgId: x.aiImgId };
    if (x.externalId) return { photoId: x.photoId ?? null, externalId: x.externalId };
  }
  const ai = AI_RE.exec(src);
  if (ai) return { aiImgId: +ai[1] };
  const ph = PHOTO_RE.exec(src);
  if (ph) return { externalId: ph[1] };
  return null;
}
export const isAiSrc = (src?: string) => AI_RE.test(src || '');

/* ── 인증이 필요한 사진 → blob 주소 (한 번 받은 건 기억) ── */
const cache = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

function needsAuth(src: string) {
  return PHOTO_RE.test(src) && src.startsWith('/api/');
}
/** 사진 하나(externalId)·크기별로 한 번만 받는다 — 받을 때는 항상 sv-portfolio 경로(apiClient 기준 /portfolio/…) */
function key(src: string, thumb: boolean) {
  const ext = PHOTO_RE.exec(src)![1];
  return `${FILES}/${ext}${thumb ? '?thumb=true' : ''}`;
}

/** 바로 쓸 수 있는 주소 — 아직 못 받았으면 null */
export function cachedUrl(src: string | undefined, thumb = false): string | null {
  if (!src) return null;
  if (!needsAuth(src)) return src;
  return cache.get(key(src, thumb)) || (thumb ? cache.get(key(src, false)) || null : null);
}

export function loadUrl(src: string, thumb = false): Promise<string> {
  if (!needsAuth(src)) return Promise.resolve(src);
  const k = key(src, thumb);
  const hit = cache.get(k);
  if (hit) return Promise.resolve(hit);
  let p = pending.get(k);
  if (!p) {
    p = apiClient
      .get<Blob>(k, { responseType: 'blob', timeout: 60000 })
      .then((res) => {
        const u = URL.createObjectURL(res.data);
        cache.set(k, u);
        return u;
      })
      .catch((e) => {
        // 썸네일이 없으면 원본으로
        if (thumb) return loadUrl(src, false);
        throw e;
      })
      .finally(() => pending.delete(k));
    pending.set(k, p);
  }
  return p;
}

/** 화면에 그릴 사진 주소 — 받는 동안은 null */
export function useImageUrl(src: string | undefined, thumb = false): string | null {
  const [url, setUrl] = useState<string | null>(() => cachedUrl(src, thumb));
  useEffect(() => {
    let alive = true;
    const now = cachedUrl(src, thumb);
    setUrl(now);
    if (src && !now) {
      loadUrl(src, thumb)
        .then((u) => alive && setUrl(u))
        .catch(() => alive && setUrl(null));
    }
    return () => {
      alive = false;
    };
  }, [src, thumb]);
  return url;
}

/** 다운로드(사진으로 저장)·표시하고 고치기에서 — 다 받아진 Image */
export function loadImage(src: string, thumb = false): Promise<HTMLImageElement> {
  return loadUrl(src, thumb).then(
    (u) =>
      new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error('사진을 불러오지 못했어요'));
        im.src = u;
      }),
  );
}
